import { createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { getOutgoers, getConnectedEdges } from "reactflow";
import { getLayoutedElements } from "../../components/flowChart/utils";
import { FLOW_CHART_NODE_TYPES, FLOW_CHART_DEFAULTS } from "../../components/flowChart/components/constants";

const CARD_WIDTH = FLOW_CHART_DEFAULTS.NODE_WIDTH;
const CARD_HEIGHT = FLOW_CHART_DEFAULTS.NODE_HEIGHT;
const EDGE_BORDER_RADIUS = FLOW_CHART_DEFAULTS.EDGE_BORDER_RADIUS;
const EDGE_LINE_TYPE = FLOW_CHART_DEFAULTS.EDGE_LINE_TYPE;

const getNodes = (nodes, edges, collapseAll = false) => {
  return nodes && nodes.length > 0
    ? nodes.map((a) => ({
      ...a,
      width: CARD_WIDTH,
      height: CARD_HEIGHT,
      position: {
        x: a.position?.x ?? 0,
        y: a.position?.y ?? 0,
      },
      data: {
        ...a.data,
        hasChildren: edges.some(
          (b) =>
            b.source === a.id &&
              nodes.find((c) => c.id == b.target)?.type ==
                FLOW_CHART_NODE_TYPES.ANSWER
        ),
        openChildren: collapseAll ? false : a.data.openChildren ?? false,
        childrenCount: edges.filter(
          (b) =>
            b.source === a.id &&
              nodes.find((c) => c.id == b.target)?.type ==
                FLOW_CHART_NODE_TYPES.ANSWER
        ).length,
      },
    }))
    : [];
};

const getEdges = (edges) => {
  return edges && edges.length > 0
    ? edges.map((a) => ({
      ...a,
      type: a.type ?? EDGE_LINE_TYPE,
      pathOptions: {
        borderRadius: EDGE_BORDER_RADIUS,
        curvature: 20,
      },
    }))
    : [];
};

//helper function used in toggleChildren reducer action
const toggleVisibiltyOfRelatedNodesAndEdges = (nodes, edges, parentNode, currentToggleValue) => {
  //toggle parent's node openChildren property
  parentNode.data.openChildren = !currentToggleValue;
  //find the children node of the parent node (answers)
  let connectedChildrenNodes = getOutgoers(parentNode, nodes, edges).filter(
    (a) => a.type == FLOW_CHART_NODE_TYPES.ANSWER
  );
  //find the connected edges to the children nodes
  let connectedChildrenEdges = getConnectedEdges(connectedChildrenNodes, edges);
  //first iterate through children nodes to toggle the visibility of node
  //and then iterate through connected edges to toggle the visibility of edges
  //eg. if parent node is collapsed then all children nodes and edges connected to children should be hidden
  for (let index = 0; index < connectedChildrenNodes.length; index++) {
    const childNode = connectedChildrenNodes[index];
    childNode.hidden = !parentNode.data.openChildren;
    for (let index = 0; index < connectedChildrenEdges.length; index++) {
      const childEdge = connectedChildrenEdges[index];
      childEdge.hidden = !parentNode.data.openChildren;
    }
  }
  //we want also to toggle the visibility of the edge connecting the two parents
  //eg. if parent node is expanded then the edge connecting the two parents should be hidden
  let parentToNextParentEdge = edges.find(
    (x) =>
      x.source === parentNode.id &&
      x.target === connectedChildrenEdges[0]?.target
  );
  if (parentToNextParentEdge) {
    parentToNextParentEdge.hidden = parentNode.data.openChildren;
  }
};

const flowBuilderSlice = createSlice({
  name: "flowBuilders",
  initialState: [],
  reducers: {
    initialize(state, action) {
      let name = action.payload.name;
      let currentFlowBuilder = state.find(
        (flowBuilder) => flowBuilder.name === name
      );

      if (!name) return state;

      if (!currentFlowBuilder) {
        return [
          ...state,
          {
            name,
            nodes: getNodes(action.payload.nodes, action.payload.edges, true),
            edges: getEdges(action.payload.edges),
          },
        ];
      }

      currentFlowBuilder.nodes = getNodes(
        action.payload.nodes,
        action.payload.edges
      );

      currentFlowBuilder.edges = getEdges(action.payload.edges);
    },
    update(state, action) {
      let currentFlowBuilder = state.find(
        (flowBuilder) => flowBuilder.name === action.payload.name
      );

      if (!currentFlowBuilder) return state;

      currentFlowBuilder.nodes = getNodes(
        action.payload.nodes,
        action.payload.edges
      );
      currentFlowBuilder.edges = getEdges(action.payload.edges);
    },
    delete(state, action) {
      return state.filter((flowBuilder) => flowBuilder.name !== action.payload);
    },
    toggleChildren(state, action) {
      let currentFlowBuilder = state.find(
        (flowBuilder) => flowBuilder.name === action.payload.name
      );

      if (!currentFlowBuilder) return state;

      let deepCopyCurrentFlowBuilder = JSON.parse(
        JSON.stringify(currentFlowBuilder)
      );

      let _nodes = deepCopyCurrentFlowBuilder.nodes;
      let _edges = deepCopyCurrentFlowBuilder.edges;
      //if toggleAll is true then we want to toggle all parent nodes and their children
      if (action.payload.toggleAll) {
        //get all parent nodes
        let parentNodes = _nodes.filter((x) => x.data.hasChildren);
        for (let index = 0; index < parentNodes.length; index++) {
          let parentNode = parentNodes[index];
          toggleVisibiltyOfRelatedNodesAndEdges(_nodes, _edges, parentNode, action.payload.currentSwitchValue);
        }
      } else {
        //find the parent node from the parent id
        let parentNode = _nodes.find((a) => a.id === action.payload.parentId);
        toggleVisibiltyOfRelatedNodesAndEdges(_nodes, _edges, parentNode, parentNode.data.openChildren);
      }

      let hiddenNodes = _nodes.filter((a) => a.hidden);
      let hiddenEdges = _edges.filter((a) => a.hidden);
      //here we re-calculate the positions of the visible nodes and edges
      const layouted = getLayoutedElements(
        _nodes.filter((a) => !a.hidden),
        _edges.filter((a) => !a.hidden)
      );
      //merge the hidden nodes and edges with the visible ones
      layouted.nodes = [...layouted.nodes, ...hiddenNodes];
      layouted.edges = [...layouted.edges, ...hiddenEdges];
      //update the new nodes and edges state properties
      currentFlowBuilder.nodes = getNodes(layouted.nodes, layouted.edges);
      currentFlowBuilder.edges = getEdges(layouted.edges);
    },
    toggleNodeStatus(state, action) {
      let currentFlowBuilder = state.find(
        (flowBuilder) => flowBuilder.name === action.payload.name
      );

      if (currentFlowBuilder) {
        let node = currentFlowBuilder.nodes.find(
          (node) => node.id === action.payload.nodeId
        );

        if (node) {
          node.data = { ...node.data, disabled: !node.data.disabled };
        }
      }
    },
    // This action is used to update the labels of the nodes in the flow builder
    syncNodeLabels(state, action) {
      let payloadNodes = action.payload.nodes ?? [];
      let currentNodes = state.find(
        (flowBuilder) => flowBuilder.name === action.payload.name
      )?.nodes ?? [];
      payloadNodes.forEach((payloadNode) => {
        // Iterate through the payload nodes
        let currentNode = currentNodes.find((node) => node.id === payloadNode.id);
        if (currentNode) {
          // Update the label of the current node with the label of the payload node by id
          currentNode.data = { ...currentNode.data, label: payloadNode.data.label };
        }
      });
    }
  },
});

export const useFlowBuildersSelector = (name) =>
  useSelector((state) =>
    state.flowBuilders.find((flowBuilder) => flowBuilder.name === name)
  );

export const flowBuildersActions = flowBuilderSlice.actions;
export default flowBuilderSlice.reducer;
